import { useCallback, useMemo, useRef } from 'react';

/**
 * useIdempotencyKey — one client-attempt identity for a protected CREATE.
 *
 * Pairs with `useSubmitGuard`: when the guard arms a create, the call site
 * mints a key and sends it as the `Idempotency-Key` request header (via the
 * per-call `headers` option on `apiPost`/`apiFetch`). The server treats the
 * header as OPTIONAL, so sending it is safe to land ahead of (or without) the
 * server-side dedupe that honours it.
 *
 * LIFECYCLE — mint on arm, clear on SUCCESS only:
 *  - `mint()` returns the CURRENT key if one is outstanding, else a fresh
 *    UUID. So a RETRY of a failed attempt (network error, non-ok response)
 *    reuses the SAME key — which is the whole point: if the first request
 *    actually landed and only its response was lost, the retry is deduped
 *    server-side instead of creating a duplicate.
 *  - `clear()` is called by the caller on a confirmed success (`res.ok`),
 *    so the NEXT create is a new attempt with a new key.
 *  - Never clear on failure; never mint per request.
 *
 * 🪤 `crypto.randomUUID` needs a secure context and a modern runtime (jsdom's
 * `crypto` lacks it in this project's test environment), so a v4 fallback is
 * included. The fallback's randomness is not cryptographic — fine here: the
 * key is a dedupe token, not a secret.
 */
// Exported (2026-09-15, R13) for the pending-upload key too — one UUID source,
// with the one jsdom fallback, rather than a second copy.
export function randomUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 shape, Math.random-backed — dedupe token only, not a secret.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function useIdempotencyKey() {
  const keyRef = useRef(null);

  /** The key for THIS attempt — a fresh one, or the outstanding one on retry. */
  const mint = useCallback(() => {
    if (!keyRef.current) keyRef.current = randomUuid();
    return keyRef.current;
  }, []);

  /** The attempt SUCCEEDED — the next create is a new attempt. */
  const clear = useCallback(() => {
    keyRef.current = null;
  }, []);

  // Referentially stable, so the pair can sit in a `useCallback`/`useEffect`
  // dependency array without re-creating the consumer every render.
  return useMemo(() => ({ mint, clear }), [mint, clear]);
}
